import type { LangMap } from "./index";

/** Analytics didattica per il docente: padronanza, errori ricorrenti, avvisi. */
const it: Record<string, unknown> = {
  title: "Analisi della classe",
  subtitle: "Dai risultati delle attività corrette in automatico: cosa è solido, cosa va ripreso.",
  refresh: "Aggiorna",
  alerts: "Avvisi",
  mastery: "Padronanza per argomento",
  errors: "Errori ricorrenti",
  students: "Studenti da seguire",
  reteach: "Da riprendere in classe",
  struggling: "In difficoltà",
  attempts: "{{correct}}/{{attempts}} risposte corrette · {{students}} studenti",
  wrongCount: "{{wrong}} sbagliate su {{attempts}} ({{rate}}%)",
  commonAnswer: "Risposta più frequente",
  expected: "Corretta",
  explain: "Spiega questo errore",
  explaining: "Analizzo...",
  saveDraft: "Salva come bozza",
  savedDraft: "Mini-lezione salvata nei materiali (bozza)",
  accuracy: "{{accuracy}}% · {{correct}}/{{answered}}",
  level: { solid: "Solido", shaky: "Incerto", weak: "Debole" },
  kpi: {
    students: "Studenti",
    assignments: "Attività",
    submissions: "Consegne",
    answered: "Risposte corrette in automatico",
  },
  empty: {
    title: "Ancora nessun dato",
    body: "Assegna un'attività con domande a correzione automatica: qui vedrai padronanza ed errori della classe.",
  },
  alert: {
    topicWeak: "«{{topic}}» è al {{mastery}}%: la classe non l'ha capito, va ripreso.",
    topicShaky: "«{{topic}}» è al {{mastery}}%: serve un ripasso mirato.",
    studentWeak: "{{name}} è al {{accuracy}}% di risposte corrette: valuta un supporto.",
    coverage: "Solo il {{coverage}}% delle consegne attese è arrivato.",
  },
};

const en: Record<string, unknown> = {
  title: "Class insights",
  subtitle: "From auto-graded activities: what's solid and what needs reteaching.",
  refresh: "Refresh",
  alerts: "Alerts",
  mastery: "Mastery by topic",
  errors: "Recurring mistakes",
  students: "Students to follow up",
  reteach: "Reteach in class",
  struggling: "Struggling",
  attempts: "{{correct}}/{{attempts}} correct · {{students}} students",
  wrongCount: "{{wrong}} wrong out of {{attempts}} ({{rate}}%)",
  commonAnswer: "Most common answer",
  expected: "Correct",
  explain: "Explain this mistake",
  explaining: "Analysing...",
  saveDraft: "Save as draft",
  savedDraft: "Mini lesson saved to materials (draft)",
  accuracy: "{{accuracy}}% · {{correct}}/{{answered}}",
  level: { solid: "Solid", shaky: "Shaky", weak: "Weak" },
  kpi: {
    students: "Students",
    assignments: "Activities",
    submissions: "Submissions",
    answered: "Auto-graded answers",
  },
  empty: {
    title: "No data yet",
    body: "Assign an activity with auto-graded questions and you'll see mastery and mistakes here.",
  },
  alert: {
    topicWeak: "“{{topic}}” is at {{mastery}}%: the class hasn't got it, reteach it.",
    topicShaky: "“{{topic}}” is at {{mastery}}%: a targeted review would help.",
    studentWeak: "{{name}} is at {{accuracy}}% correct: consider extra support.",
    coverage: "Only {{coverage}}% of the expected submissions came in.",
  },
};

const es: Record<string, unknown> = {
  ...en,
  title: "Análisis de la clase",
  refresh: "Actualizar",
  alerts: "Avisos",
  mastery: "Dominio por tema",
  errors: "Errores recurrentes",
  students: "Alumnos a seguir",
  reteach: "Repasar en clase",
  explain: "Explica este error",
  explaining: "Analizando...",
  saveDraft: "Guardar como borrador",
  level: { solid: "Sólido", shaky: "Inseguro", weak: "Débil" },
};

const fr: Record<string, unknown> = {
  ...en,
  title: "Analyse de la classe",
  refresh: "Actualiser",
  alerts: "Alertes",
  mastery: "Maîtrise par thème",
  errors: "Erreurs récurrentes",
  students: "Élèves à suivre",
  reteach: "À reprendre en classe",
  explain: "Explique cette erreur",
  explaining: "Analyse...",
  saveDraft: "Enregistrer en brouillon",
  level: { solid: "Solide", shaky: "Fragile", weak: "Faible" },
};

const de: Record<string, unknown> = {
  ...en,
  title: "Klassenanalyse",
  refresh: "Aktualisieren",
  alerts: "Hinweise",
  mastery: "Beherrschung nach Thema",
  errors: "Wiederkehrende Fehler",
  students: "Zu begleitende Lernende",
  reteach: "Im Unterricht wiederholen",
  explain: "Diesen Fehler erklären",
  explaining: "Analysiere...",
  saveDraft: "Als Entwurf speichern",
  level: { solid: "Sicher", shaky: "Wackelig", weak: "Schwach" },
};

const pt: Record<string, unknown> = {
  ...en,
  title: "Análise da turma",
  refresh: "Atualizar",
  alerts: "Alertas",
  mastery: "Domínio por tema",
  errors: "Erros recorrentes",
  students: "Alunos a acompanhar",
  reteach: "Rever na aula",
  explain: "Explica este erro",
  explaining: "A analisar...",
  saveDraft: "Guardar como rascunho",
  level: { solid: "Sólido", shaky: "Instável", weak: "Fraco" },
};

export const analytics: LangMap = { it, en, es, fr, de, pt };
